import { useMemo } from 'react';
import { useCurrency } from '../../hooks';
import {
  CustomerBalanceTransactionStatusEnum,
  CustomerBalanceTransactionTypeEnum,
  type CustomerTransaction,
} from '../../types';
import { InlineFormattedNumber } from '../FormattedNumber';
import { FormattedMessage } from '../Intl';
import { getFriendlyTransferStatus, transferStatusMessages } from '../Status';
import { Text } from '../Text';
import { messages } from './messages';

export const CustomerTransactionLabel = ({ entity }: { entity: CustomerTransaction }) => {
  const currencyInfo = useCurrency(entity.Currency);

  // Helper to render quantity with proper formatting
  const quantityRenderer = useMemo(
    () => () => (
      <InlineFormattedNumber
        number={entity.Quantity}
        currency={entity.Currency}
        increment={currencyInfo?.DefaultIncrement}
      />
    ),
    [entity.Quantity, entity.Currency, currencyInfo?.DefaultIncrement]
  );

  // Helper to get transfer status labels (common across Dealer and Customer)
  const getTransferStatusLabel = (status: CustomerBalanceTransactionStatusEnum) => {
    switch (status) {
      case CustomerBalanceTransactionStatusEnum.TransferInitiated:
        return <FormattedMessage {...transferStatusMessages.transferInitiated} />;
      case CustomerBalanceTransactionStatusEnum.TransferRegistered:
        return <FormattedMessage {...messages.transferRegistered} />;
      case CustomerBalanceTransactionStatusEnum.TransferPendingBlockchainConfirmation:
        return <FormattedMessage {...transferStatusMessages.transferPendingBlockchainConfirmation} />;
      default:
        return null;
    }
  };

  switch (entity.Initiator) {
    // Transaction Label for Initiator=Dealer
    case 'Dealer':
      switch (entity.TransactionType) {
        case CustomerBalanceTransactionTypeEnum.Deposit:
          switch (entity.Status) {
            case CustomerBalanceTransactionStatusEnum.New:
            case CustomerBalanceTransactionStatusEnum.PendingApproval:
              return (
                <Text>
                  <FormattedMessage
                    {...messages.userEnteredTypeForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                      user: entity.InitiatorUser || 'Unknown',
                    }}
                  />
                </Text>
              );
            case CustomerBalanceTransactionStatusEnum.Completed:
              if (entity.DealerUser) {
                return (
                  <Text>
                    <FormattedMessage
                      {...messages.userApprovedTypeForQuantity}
                      values={{
                        quantity: quantityRenderer,
                        transactionType: entity.TransactionType,
                        user: entity.DealerUser,
                      }}
                    />
                  </Text>
                );
              }
              return (
                <Text>
                  <FormattedMessage
                    {...messages.completedTypeForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                    }}
                  />
                </Text>
              );
            case CustomerBalanceTransactionStatusEnum.Rejected:
              return (
                <Text>
                  <FormattedMessage
                    {...messages.userRejectedTypeRequest}
                    values={{
                      transactionType: entity.TransactionType,
                      user: entity.DealerUser || 'Unknown',
                    }}
                  />
                </Text>
              );
            default:
              return <Text>{getFriendlyTransferStatus(entity.Status)}</Text>;
          }
        case CustomerBalanceTransactionTypeEnum.Withdrawal:
        case CustomerBalanceTransactionTypeEnum.ForceWithdrawal:
          switch (entity.Status) {
            case CustomerBalanceTransactionStatusEnum.New:
            case CustomerBalanceTransactionStatusEnum.PendingApproval:
              return (
                <Text>
                  <FormattedMessage
                    {...messages.userEnteredTypeForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                      user: entity.InitiatorUser || 'Unknown',
                    }}
                  />
                </Text>
              );
            case CustomerBalanceTransactionStatusEnum.PendingTransfer:
              // Show who approved it (moved it to PendingTransfer)
              if (entity.DealerUser) {
                return (
                  <Text>
                    <FormattedMessage
                      {...messages.userApprovedTypeForQuantity}
                      values={{
                        quantity: quantityRenderer,
                        transactionType: entity.TransactionType,
                        user: entity.DealerUser,
                      }}
                    />
                  </Text>
                );
              }
              return (
                <Text>
                  <FormattedMessage
                    {...messages.pendingTransferForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                    }}
                  />
                </Text>
              );
            case CustomerBalanceTransactionStatusEnum.TransferInitiated:
            case CustomerBalanceTransactionStatusEnum.TransferRegistered:
            case CustomerBalanceTransactionStatusEnum.TransferPendingBlockchainConfirmation:
              return <Text>{getTransferStatusLabel(entity.Status)}</Text>;
            case CustomerBalanceTransactionStatusEnum.Completed:
              if (entity.TreasuryUser) {
                return (
                  <Text>
                    <FormattedMessage
                      {...messages.userConfirmedTransferForQuantity}
                      values={{
                        quantity: quantityRenderer,
                        user: entity.TreasuryUser,
                      }}
                    />
                  </Text>
                );
              }
              return (
                <Text>
                  <FormattedMessage
                    {...messages.completedTypeForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                    }}
                  />
                </Text>
              );
            case CustomerBalanceTransactionStatusEnum.Rejected: {
              // Show which user rejected it
              const rejectorUser = entity.DealerUser || entity.TreasuryUser;
              if (rejectorUser) {
                return (
                  <Text>
                    <FormattedMessage
                      {...messages.userRejectedTypeRequest}
                      values={{
                        transactionType: entity.TransactionType,
                        user: rejectorUser,
                      }}
                    />
                  </Text>
                );
              }
              return (
                <Text>
                  <FormattedMessage
                    {...messages.rejectedTypeForQuantity}
                    values={{
                      quantity: quantityRenderer,
                      transactionType: entity.TransactionType,
                    }}
                  />
                </Text>
              );
            }
            default:
              return <Text>{getFriendlyTransferStatus(entity.Status)}</Text>;
          }
        default:
          return <Text>{getFriendlyTransferStatus(entity.Status)}</Text>;
      }

    // Transaction Label for Initiator=Customer
    case 'Customer':
      switch (entity.Status) {
        case CustomerBalanceTransactionStatusEnum.New:
        case CustomerBalanceTransactionStatusEnum.PendingApproval:
          return (
            <Text>
              <FormattedMessage
                {...messages.userEnteredTypeForQuantity}
                values={{
                  quantity: quantityRenderer,
                  transactionType: entity.TransactionType,
                  user: entity.InitiatorUser || 'Customer',
                }}
              />
            </Text>
          );
        case CustomerBalanceTransactionStatusEnum.PendingTransfer:
          if (entity.DealerUser) {
            return (
              <Text>
                <FormattedMessage
                  {...messages.userApprovedTypeForQuantity}
                  values={{
                    quantity: quantityRenderer,
                    transactionType: entity.TransactionType,
                    user: entity.DealerUser,
                  }}
                />
              </Text>
            );
          }
          return (
            <Text>
              <FormattedMessage
                {...messages.pendingTransferForQuantity}
                values={{
                  quantity: quantityRenderer,
                  transactionType: entity.TransactionType,
                }}
              />
            </Text>
          );
        case CustomerBalanceTransactionStatusEnum.TransferInitiated:
        case CustomerBalanceTransactionStatusEnum.TransferRegistered:
        case CustomerBalanceTransactionStatusEnum.TransferPendingBlockchainConfirmation:
          return <Text>{getTransferStatusLabel(entity.Status)}</Text>;
        case CustomerBalanceTransactionStatusEnum.Completed:
          if (entity.TreasuryUser) {
            return (
              <Text>
                <FormattedMessage
                  {...messages.userConfirmedTransferForQuantity}
                  values={{
                    quantity: quantityRenderer,
                    user: entity.TreasuryUser,
                  }}
                />
              </Text>
            );
          }
          return (
            <Text>
              <FormattedMessage
                {...messages.completedTypeForQuantity}
                values={{
                  quantity: quantityRenderer,
                  transactionType: entity.TransactionType,
                }}
              />
            </Text>
          );
        case CustomerBalanceTransactionStatusEnum.Rejected:
          return (
            <Text>
              <FormattedMessage
                {...messages.userRejectedTypeRequest}
                values={{
                  transactionType: entity.TransactionType,
                  user: entity.TreasuryUser || entity.DealerUser || 'System',
                }}
              />
            </Text>
          );
        default:
          return <Text>{getFriendlyTransferStatus(entity.Status)}</Text>;
      }

    // Fallback
    default:
      return <Text>{getFriendlyTransferStatus(entity.Status)}</Text>;
  }
};
